import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, Flag, MapPin, PackagePlus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAuthStore } from "@/store/auth";
import { ContractorSuggestionPanel } from "@/features/contractors/components/ContractorSuggestionPanel";
import type { SuggestScope } from "@/features/contractors/api/knowledgeNotes";
import { sortLeaveLast, withLeaveSuffix } from "@/features/contractors/api/leaves";
import {
  useActiveContractors, useCheckpointTemplate, useContractorAgreements, useDesignContractors,
} from "../hooks";
import { apiErr } from "../utils";
import { autoTriggerLabel } from "@/features/orders/components/AutoLineItemRow";
import {
  emptyProjectFileShare, ProjectFileShareFields, projectFileShareFields, projectFileShareValid,
} from "./ProjectFileShareFields";
import type { ProjectFileShareValue } from "./ProjectFileShareFields";
import type { MdcpAiSuggestion } from "../api";
import type { DesignOrderLink } from "../types";

export interface ContractorPickOption {
  value: string;
  label: string;
  /** Extra context shown under the picker once selected (e.g. distance to project). */
  hint?: string;
}

export interface ContractorsOverride {
  options: ContractorPickOption[];
  loading: boolean;
  emptyText?: string;
  loadingText?: string;
  /** Shown above the picker once loaded — e.g. the MDCP order area + ranking rule. */
  note?: string;
}

interface DesignOrderFormProps {
  pending: boolean;
  kind?: "primary" | "correction";
  onSubmit: (fd: FormData) => void;
  onCancel?: () => void;
  /** Optional AI pre-fill: runs after picking a contractor and fills quantities for the
   *  matching framework-agreement positions (still fully editable). On failure the form
   *  shows a warning and the user selects positions manually. */
  aiSuggest?: (contractorId: string) => Promise<MdcpAiSuggestion>;
  /** Pre-selected contractor (e.g. "Zleć tej firmie" from the Wody price simulation):
   *  the picker starts on this company and the AI pre-fill fires immediately. */
  defaultContractorId?: string;
  /** Optional replacement for the default active-contractors list — e.g. the MDCP form
   *  passes only companies with MDCP-ish framework positions, labelled with distance. */
  contractorsOverride?: ContractorsOverride;
  /** ZE technical order: show the PT/PAB/PZT doc-type selector + hard-filter the contractor
   *  picker to design subcontractors (lexical match on framework positions), and render the
   *  predefined checkpoint set (editable dates + include toggles → sent as `checkpoints`). */
  design?: boolean;
  /** Enables the predefined checkpoint set for a specific order path ("technical" |
   *  "cost_estimate" | "mdcp") independently of `design`. MDCP passes "mdcp" (→ „Mapa robocza"
   *  +7 dni) without the design-contractor hard filter. When omitted but `design` is set, the
   *  technical set is used (backward compat). */
  checkpointPath?: string;
  /** PT-only: repository resources for the „szablony PZT/PAB/PT” reference picker. When
   *  provided, the form shows the picker (pre-selected with defaultTemplateResourceId) and
   *  sends `template_resource`. */
  templateResources?: { value: string; label: string }[];
  defaultTemplateResourceId?: string;
  /** PT / Kosztorys: show the „dołącz dokumenty przetargowe” checkbox (sends `attach_tender_docs`). */
  showTenderDocs?: boolean;
  /** PT / Kosztorys: show the „dołącz aktualną trasę (DXF)” checkbox — the most current route
   *  version of the binding variant, i.e. the agreed concept route (sends `attach_route_dxf`). */
  showRouteDxf?: boolean;
  /** ZE connection id — enables the coordinator picker + "orders under coordinator" counts. */
  connectionId?: string;
  /** Rodzaj zlecenia dla panelu „Podpowiedź podwykonawcy" (historia na projekcie + Zasady
   *  podpowiadania + AI) pod pickerem firmy. Domyślnie z `checkpointPath` albo „technical"
   *  przy `design`; podaj jawnie, gdy żadne z nich nie pasuje. */
  suggestScope?: SuggestScope;
  /** Show the „udostępnij pliki z projektu ZE” section: share the project's Pliki tab into
   *  the order (all non-confidential folders, or a hand-picked set). Sends
   *  `share_project_files` (+ `share_all_nonconfidential` / `share_folder_slugs`).
   *  Requires `connectionId` (folder list). */
  showProjectFileShare?: boolean;
  /** Tryb „Zleć zwycięzcy": wykonawcę i kwotę bierzemy z rozstrzygniętego postępowania
   *  ofertowego, a nie z ręki. Picker firm i pozycje z umowy ramowej znikają — wykonawca
   *  z przetargu umowy ramowej mieć nie musi, a cena wyszła z konkurencji ofert. Pliki
   *  wejściowe idą z zamrożonego kompletu ogłoszenia, więc znikają też checkboxy załączników. */
  winner?: {
    proceedingId: string;
    number: string;
    title: string;
    contractorName: string;
    amountNet: string;
  };
}

// Inline subcontractor-order form for a design stage: pick a contractor + framework-agreement
// line items (qty) + a deadline; the backend approves it into an Order in one go and links it
// here. Only framework-agreement items (no custom lines) so a kierownik may approve too.
// Single-column layout on purpose — the form lives in narrow sidebars/cards, where viewport
// breakpoints (sm:grid-cols-2) would wrongly squeeze it into two cramped columns.
const isoInDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function DesignOrderForm({ pending, kind = "primary", onSubmit, onCancel, aiSuggest, defaultContractorId, contractorsOverride, templateResources, defaultTemplateResourceId, showTenderDocs, showRouteDxf, design, checkpointPath, connectionId, showProjectFileShare, winner, suggestScope }: DesignOrderFormProps) {
  const { user } = useAuthStore();
  // Checkpoints are a separate concern from `design` (contractor filter). Enabled when a path
  // is given, or implicitly for legacy `design` callers (→ technical set).
  const cpEnabled = !!design || !!checkpointPath;
  const cpPath = checkpointPath ?? "technical";
  // ZE design mode: hard filter to design-documentation subcontractors (whole package — no
  // doc-type split) + workload counts + checkpoint template.
  // Whom the "orders under this coordinator" count is computed for (Project.coordinators).
  const [coordinatorId, setCoordinatorId] = useState("");
  // „Pokaż wszystkich aktywnych podwykonawców”: the picker then lists every active company
  // and says, per company, WHY it was not proposed (no price-list positions, none with the
  // category „Dokumentacja projektowa”, or positions that only read like design work).
  const [showAllContractors, setShowAllContractors] = useState(false);
  const designContractorsQ = useDesignContractors(
    { coordinator: coordinatorId || undefined, connection: connectionId, all: showAllContractors || undefined },
    !!design,
  );
  const coordinators = designContractorsQ.data?.coordinators ?? [];
  // Default the "count under" person to the current user if they coordinate this
  // przedsięwzięcie, else the first coordinator — the most useful "have I worked with them" view.
  useEffect(() => {
    if (!design || coordinatorId || !coordinators.length) return;
    const mine = coordinators.find((c) => c.id === user?.id);
    setCoordinatorId(mine?.id ?? coordinators[0].id);
  }, [design, coordinators, coordinatorId, user?.id]);

  const checkpointTemplateQ = useCheckpointTemplate(cpPath, cpEnabled);
  const [checkpoints, setCheckpoints] = useState<Record<string, { enabled: boolean; due: string }>>({});
  useEffect(() => {
    if (!cpEnabled || !checkpointTemplateQ.data) return;
    setCheckpoints((prev) =>
      Object.keys(prev).length
        ? prev
        : Object.fromEntries(
            checkpointTemplateQ.data.map((c) => [c.code, { enabled: true, due: isoInDays(c.offset_days) }]),
          ),
    );
  }, [design, checkpointTemplateQ.data]);

  const designOverride: ContractorsOverride | undefined = design
    ? {
        options: (designContractorsQ.data?.contractors ?? []).map((c) => ({
          value: c.id,
          // Workload right in the option so the picker can favour the least-loaded /
          // already-worked-with projektant: Σ total active, count under the coordinator,
          // and the rating (GK-only data — contractors cannot reach this endpoint).
          label:
            `${c.name} · aktywne Σ ${c.active_orders_total}` +
            (c.active_orders_coordinator != null ? ` · u koord. ${c.active_orders_coordinator}` : "") +
            (c.rating != null ? ` · ocena ${c.rating.toFixed(2)}` : "") +
            (c.eligible ? "" : " · niezaproponowany"),
          hint: c.eligible
            ? c.matched_items.length
              ? `Pozycje projektowe: ${c.matched_items.join("; ")}`
              : undefined
            : `Firma nie została zaproponowana: ${c.reason_label ?? "brak pozycji dokumentacji projektowej"}.` +
              (c.lookalike_items.length ? ` Pozycje bez kategorii: ${c.lookalike_items.join("; ")}` : ""),
        })),
        loading: designContractorsQ.isLoading,
        emptyText: showAllContractors
          ? "Brak aktywnych podwykonawców."
          : "Żaden aktywny podwykonawca nie ma w umowie ramowej pozycji z kategorią „Dokumentacja projektowa”. " +
            "Kategorię ustawia się w cenniku umowy ramowej; włącz „Pokaż wszystkich”, żeby zobaczyć powody.",
        loadingText: showAllContractors
          ? "Wczytuję wszystkich aktywnych podwykonawców…"
          : "Szukam projektantów z pozycjami dokumentacji projektowej w umowach ramowych…",
        note: showAllContractors
          ? "Lista wszystkich aktywnych podwykonawców. Firmy oznaczone „niezaproponowany” nie mają w umowie ramowej pozycji z kategorią „Dokumentacja projektowa” — po wybraniu zobaczysz powód."
          : undefined,
      }
    : undefined;
  const override = designOverride ?? contractorsOverride;

  const { data: contractors = [] } = useActiveContractors(!override);
  const [contractor, setContractor] = useState("");
  const contractorRef = useRef("");
  const { data: agreements = [], isLoading: loadingAgreements } = useContractorAgreements(contractor || undefined);
  const [qty, setQty] = useState<Record<string, string>>({});
  // Default deadline: +30 days (local date, editable).
  const [deadline, setDeadline] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [description, setDescription] = useState("");
  // Oferta to jedna kwota netto bez pozycji, a zamówienie liczy wartość z pozycji — startujemy
  // od jednej pozycji na kwotę oferty; kupujący może ją rozbić przed zatwierdzeniem.
  const [wonItems, setWonItems] = useState(() =>
    winner
      ? [{ name: `${winner.title} (${winner.number})`, unit: "usługa", qty: "1", rate: winner.amountNet }]
      : [],
  );

  // ZE design order: the completion deadline (termin realizacji) always equals the latest
  // enabled checkpoint — keep it in sync when checkpoints (dates / include toggles) change.
  const latestCheckpointDue = useMemo(() => {
    if (!cpEnabled) return null;
    const dues = (checkpointTemplateQ.data ?? [])
      .filter((c) => (checkpoints[c.code] ?? { enabled: true }).enabled)
      .map((c) => checkpoints[c.code]?.due || isoInDays(c.offset_days));
    return dues.length ? dues.reduce((a, b) => (a > b ? a : b)) : null;
  }, [design, checkpointTemplateQ.data, checkpoints]);
  useEffect(() => {
    if (latestCheckpointDue) setDeadline(latestCheckpointDue);
  }, [latestCheckpointDue]);
  const [isPaid, setIsPaid] = useState(true);
  // PT-only: referencja do szablonów PZT/PAB/PT (repozytorium) + dołączanie dokumentów przetargowych.
  const [templateResource, setTemplateResource] = useState(defaultTemplateResourceId ?? "");
  const [attachTenderDocs, setAttachTenderDocs] = useState(true);
  // PT-only: dołącz najaktualniejszą trasę (DXF) bieżącego wariantu — uzgodniona koncepcja.
  const [attachRouteDxf, setAttachRouteDxf] = useState(true);
  const [aiReasons, setAiReasons] = useState<Record<string, string>>({});
  const [aiNote, setAiNote] = useState("");
  const [aiWarning, setAiWarning] = useState("");
  // Udostępnienie zakładki „Pliki" projektu w zamówieniu (żywa projekcja, bez kopii).
  const [share, setShare] = useState<ProjectFileShareValue>(emptyProjectFileShare);

  const suggestM = useMutation({
    mutationFn: (contractorId: string) => aiSuggest!(contractorId),
    onSuccess: (data, contractorId) => {
      if (contractorId !== contractorRef.current) return; // stale — contractor changed meanwhile
      const filled: Record<string, string> = {};
      const reasons: Record<string, string> = {};
      for (const item of data.items) {
        filled[item.line_item_type] = String(item.quantity);
        if (item.reason) reasons[item.line_item_type] = item.reason;
      }
      // User-typed quantities win over the proposal that arrived later.
      setQty((prev) => ({ ...filled, ...prev }));
      setAiReasons(reasons);
      setAiNote(data.note || "Pozycje uzupełnione przez AI — sprawdź ilości przed zatwierdzeniem.");
      setAiWarning("");
    },
    onError: (err, contractorId) => {
      if (contractorId !== contractorRef.current) return;
      setAiWarning(apiErr(err, "Podpowiedź AI nie powiodła się — dobierz pozycje ręcznie."));
    },
  });

  // Bez nadpisanego kręgu: firma na urlopie w oknie składania zamówienia idzie na dół
  // pickera z dopiskiem „(urlop od–do)" — nadal do wyboru.
  const contractorOptions = useMemo(
    () =>
      override
        ? override.options
        : sortLeaveLast(
            contractors
              .filter((c) => c.is_active)
              .map((c) => ({ value: c.id, label: withLeaveSuffix(c.name, c.on_leave), onLeave: !!c.on_leave })),
          ),
    [contractors, override],
  );
  const contractorHint = override?.options.find((o) => o.value === contractor)?.hint;
  // Podpowiedź firmy: krąg = firmy z pickera (nadpisany albo wszystkie aktywne).
  const SUGGEST_SCOPES: SuggestScope[] = ["wpw", "mdcp", "technical", "cost_estimate", "wody", "standard"];
  const resolvedSuggestScope: SuggestScope | undefined =
    suggestScope ??
    (checkpointPath && (SUGGEST_SCOPES as string[]).includes(checkpointPath)
      ? (checkpointPath as SuggestScope)
      : design
        ? "technical"
        : undefined);
  const candidateIds = useMemo(() => contractorOptions.map((o) => o.value), [contractorOptions]);
  const lineTypes = useMemo(
    () => agreements.flatMap((a) => a.line_item_types.map((t) => ({ ...t, agreement: a.title }))),
    [agreements],
  );

  const items = lineTypes
    .map((t) => ({ id: t.id, q: parseFloat(qty[t.id] || "0") }))
    .filter((x) => x.q > 0);
  const wonValid = wonItems.filter((i) => i.name.trim() && parseFloat(i.qty || "0") > 0);
  const shareValid = projectFileShareValid(share);
  const canSubmit = winner
    ? wonValid.length > 0 && !!deadline && shareValid && !pending
    : !!contractor && !!deadline && items.length > 0 && shareValid && !pending;

  function pickContractor(id: string) {
    contractorRef.current = id;
    setContractor(id);
    setQty({});
    setAiReasons({});
    setAiNote("");
    setAiWarning("");
    if (id && aiSuggest) suggestM.mutate(id);
  }

  // Preselekcja wykonawcy (raz, przy montażu) — klik „Zleć tej firmie" nie może
  // otwierać formularza z pustym pickerem, który trzeba przeklikać jeszcze raz.
  const defaultPickedRef = useRef(false);
  useEffect(() => {
    if (defaultContractorId && !defaultPickedRef.current && !contractor) {
      defaultPickedRef.current = true;
      pickContractor(defaultContractorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultContractorId]);

  function submit() {
    const fd = new FormData();
    fd.append("deadline", deadline);
    if (description.trim()) fd.append("description", description.trim());
    fd.append("is_paid", isPaid ? "true" : "false");
    if (winner) {
      // Wykonawcę i rodzaj powiązania ustala serwer z postępowania — front ich nie podmienia.
      fd.append("proceeding", winner.proceedingId);
      fd.append(
        "line_items",
        JSON.stringify(
          wonValid.map((i) => ({
            custom_name: i.name.trim(),
            custom_unit: i.unit.trim(),
            custom_unit_rate: i.rate || "0",
            quantity: i.qty,
          })),
        ),
      );
    } else {
      fd.append("contractor", contractor);
      fd.append("link_kind", kind);
      fd.append("line_items", JSON.stringify(items.map((x) => ({ line_item_type: x.id, quantity: x.q }))));
    }
    if (templateResources && templateResource) fd.append("template_resource", templateResource);
    if (showTenderDocs) fd.append("attach_tender_docs", attachTenderDocs ? "true" : "false");
    if (showRouteDxf) fd.append("attach_route_dxf", attachRouteDxf ? "true" : "false");
    if (showProjectFileShare) {
      for (const [k, v] of Object.entries(projectFileShareFields(share))) fd.append(k, v);
    }
    if (cpEnabled) {
      const selected = (checkpointTemplateQ.data ?? [])
        .filter((c) => (checkpoints[c.code] ?? { enabled: true }).enabled)
        .map((c) => ({ code: c.code, due_date: checkpoints[c.code]?.due || isoInDays(c.offset_days) }));
      fd.append("checkpoints", JSON.stringify(selected));
    }
    onSubmit(fd);
  }

  return (
    <div className={design
      ? "space-y-4 rounded-xl border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] p-4 shadow-sm"
      : "space-y-3 rounded-md border border-[var(--color-border-muted)] p-3"}>
      {design && (
        <div className="flex items-start gap-2.5 border-b border-[var(--color-border-muted)] pb-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
            <PackagePlus size={16} />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[var(--color-text)]">Zleć projektantowi</div>
            <div className="text-[11px] leading-snug text-[var(--color-text-muted)]">
              Cały komplet dokumentacji projektowej. Lista twardo filtrowana po pozycjach typu
              „Opracowanie dokumentacji projektowej…” w umowie ramowej.
            </div>
          </div>
        </div>
      )}
      {design && coordinators.length > 1 && (
        <div className="rounded-lg border border-[var(--color-border-muted)] bg-[var(--color-bg-surface)] p-2.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)] mb-1.5">
            Licz obłożenie pod koordynatora
          </div>
          <SearchableSelect
            options={coordinators.map((c) => ({ value: c.id, label: c.name }))}
            value={coordinatorId}
            onChange={(e) => setCoordinatorId(e.target.value)}
          />
          <p className="mt-1 text-[11px] leading-snug text-[var(--color-text-muted)]">
            Kolumna „u koord." pokazuje aktywne zamówienia projektanta na przedsięwzięciach tej osoby.
          </p>
        </div>
      )}
      {winner ? (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 p-2.5 dark:border-emerald-800 dark:bg-emerald-900/15">
          <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            Zwycięzca postępowania {winner.number}
          </div>
          <div className="text-sm font-semibold text-[var(--color-text)]">{winner.contractorName}</div>
          <div className="text-[11px] text-[var(--color-text-muted)]">
            Oferta {winner.amountNet} zł netto. Wykonawcy nie da się tu podmienić — bierze go
            serwer z wygranej oferty. Pliki wejściowe idą z zamrożonego kompletu z ogłoszenia.
          </div>
        </div>
      ) : (
      <div>
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            {design ? "Projektant (podwykonawca)" : "Podwykonawca"}
          </div>
          {design && (
            <label className="flex cursor-pointer select-none items-center gap-1.5 text-[11px] text-[var(--color-text-secondary)]">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={showAllContractors}
                onChange={(e) => setShowAllContractors(e.target.checked)}
              />
              Pokaż wszystkich aktywnych podwykonawców
            </label>
          )}
        </div>
        {override?.loading ? (
          <p className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
            <Sparkles size={12} className="animate-pulse shrink-0" />
            {override.loadingText ?? "Szukam firm z pozycjami MDCP w umowach ramowych…"}
          </p>
        ) : override && contractorOptions.length === 0 ? (
          <p className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
            <AlertTriangle size={13} className="mt-px shrink-0" />
            {override.emptyText ?? "Brak aktywnych podwykonawców z pasującymi pozycjami w umowie ramowej."}
          </p>
        ) : (
          <>
            {override?.note && (
              <p className="mb-1 text-[11px] leading-snug text-[var(--color-text-muted)]">{override.note}</p>
            )}
            <SearchableSelect
              options={contractorOptions}
              value={contractor}
              placeholder="— wybierz podwykonawcę —"
              onChange={(e) => pickContractor(e.target.value)}
            />
          </>
        )}
        {contractorHint && (
          <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-[var(--color-text-secondary)]">
            <MapPin size={12} className="mt-px shrink-0 text-[var(--color-text-muted)]" /> {contractorHint}
          </p>
        )}
        {resolvedSuggestScope && connectionId && candidateIds.length > 0 && (
          <div className="mt-2">
            <ContractorSuggestionPanel
              scope={resolvedSuggestScope}
              connectionId={connectionId}
              candidates={candidateIds}
              selectedId={contractor || undefined}
              onPick={pickContractor}
              enabled={!override?.loading}
              deadline={deadline || undefined}
            />
          </div>
        )}
      </div>
      )}
      <div>
        <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)] mb-1">Termin realizacji</div>
        <DatePicker
          className="disabled:opacity-70 disabled:cursor-not-allowed"
          value={deadline}
          disabled={cpEnabled && !!latestCheckpointDue}
          onChange={setDeadline}
        />
        {cpEnabled && !!latestCheckpointDue && (
          <p className="mt-1 text-[11px] leading-snug text-[var(--color-text-muted)]">
            Automatycznie równy ostatniemu checkpointowi — zmień datę checkpointu, aby przesunąć termin.
          </p>
        )}
      </div>

      {winner && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            Pozycje zamówienia (z kwoty oferty)
          </div>
          <ul className="space-y-1.5">
            {wonItems.map((item, idx) => (
              <li key={idx} className="space-y-1 rounded border border-[var(--color-border-muted)] px-2 py-1.5">
                <input
                  className="input-base w-full text-xs"
                  value={item.name}
                  placeholder="Nazwa pozycji"
                  onChange={(e) =>
                    setWonItems((p) => p.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))
                  }
                />
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  <input
                    type="number" min="0" step="any"
                    className="input-base w-20 py-1 text-xs"
                    value={item.qty}
                    onChange={(e) =>
                      setWonItems((p) => p.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))
                    }
                  />
                  <input
                    className="input-base w-24 py-1 text-xs"
                    value={item.unit}
                    placeholder="jm."
                    onChange={(e) =>
                      setWonItems((p) => p.map((x, i) => (i === idx ? { ...x, unit: e.target.value } : x)))
                    }
                  />
                  <span className="text-[var(--color-text-muted)]">×</span>
                  <input
                    type="number" min="0" step="0.01"
                    className="input-base w-28 py-1 text-xs"
                    value={item.rate}
                    onChange={(e) =>
                      setWonItems((p) => p.map((x, i) => (i === idx ? { ...x, rate: e.target.value } : x)))
                    }
                  />
                  <span className="text-[var(--color-text-muted)]">zł</span>
                  {wonItems.length > 1 && (
                    <button
                      type="button"
                      className="ml-auto text-[11px] text-[var(--color-text-muted)] hover:underline"
                      onClick={() => setWonItems((p) => p.filter((_, i) => i !== idx))}
                    >
                      usuń
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="text-[11px] text-[var(--color-primary)] hover:underline"
            onClick={() => setWonItems((p) => [...p, { name: "", unit: "usługa", qty: "1", rate: "0" }])}
          >
            + rozbij na kolejną pozycję
          </button>
          <p className="text-[10px] leading-snug text-[var(--color-text-muted)]">
            Suma pozycji powinna odpowiadać kwocie wygranej oferty ({winner.amountNet} zł netto).
          </p>
        </div>
      )}

      {!winner && contractor && (
        <div className="space-y-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            Pozycje z umowy ramowej
          </div>
          {aiSuggest && suggestM.isPending && (
            <div className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-muted)] px-2.5 py-2 text-xs">
              <Spinner size="sm" />
              <span>
                <b>AI dobiera pozycje i ilości z umowy ramowej…</b>{" "}
                <span className="text-[var(--color-text-muted)]">(kilka–kilkanaście sekund; wynik można potem edytować)</span>
              </span>
            </div>
          )}
          {aiWarning && (
            <p className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-400">
              <AlertTriangle size={12} className="mt-px shrink-0" /> {aiWarning}
            </p>
          )}
          {aiNote && !suggestM.isPending && (
            <p className="flex items-start gap-1.5 text-[11px] text-[var(--color-text-secondary)]">
              <Sparkles size={12} className="mt-px shrink-0 text-[var(--color-primary)]" /> {aiNote}
            </p>
          )}
          {loadingAgreements ? (
            <p className="text-xs text-[var(--color-text-muted)]">Ładowanie pozycji…</p>
          ) : lineTypes.length === 0 ? (
            <p className="text-xs text-amber-600">
              Podwykonawca nie ma pozycji w umowie ramowej — uzupełnij w module Podwykonawcy / Umowy ramowe.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5">
              {lineTypes.map((t) => (
                <li
                  key={t.id}
                  className={`rounded border px-2 py-1.5 ${
                    parseFloat(qty[t.id] || "0") > 0
                      ? "border-[var(--color-primary)]/40 bg-[var(--color-bg-elevated)]"
                      : "border-[var(--color-border-muted)]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs leading-snug text-[var(--color-text)] break-words min-w-0">
                      {t.name}
                      {!!(t.auto_trigger_names?.length || t.auto_trigger_name) && (
                        <span className="ml-1 text-[10px] text-sky-700 dark:text-sky-300">
                          · dodawana automatycznie przy {autoTriggerLabel(t)}
                        </span>
                      )}
                    </span>
                    {t.base_rate && (
                      <span className="shrink-0 font-mono text-[11px] text-[var(--color-text-muted)]">
                        {t.base_rate} zł
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className="input-base text-xs w-20 py-1"
                      value={qty[t.id] ?? ""}
                      placeholder="0"
                      onChange={(e) => setQty((p) => ({ ...p, [t.id]: e.target.value }))}
                    />
                    <span className="text-[var(--color-text-muted)]">{t.unit}</span>
                    {aiReasons[t.id] && qty[t.id] && (
                      <span
                        className="ml-auto inline-flex cursor-help items-center gap-1 text-[10px] text-[var(--color-text-muted)]"
                        title={aiReasons[t.id]}
                      >
                        <Sparkles size={11} className="text-[var(--color-primary)]" /> AI
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <input
        className="input-base text-xs w-full"
        placeholder="Opis / zakres zlecenia (opcjonalnie)…"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      {cpEnabled && (checkpointTemplateQ.data?.length ?? 0) > 0 && (
        <div className="rounded-lg border border-[var(--color-border-muted)] bg-[var(--color-bg-surface)] p-3">
          <div className="mb-0.5 flex items-center gap-1.5 text-xs font-semibold text-[var(--color-text)]">
            <Flag size={13} className="text-[var(--color-primary)]" /> Checkpointy zlecenia
          </div>
          <p className="mb-2.5 text-[11px] leading-snug text-[var(--color-text-muted)]">
            Terminy liczone od daty zamówienia — odznacz zbędne, dostosuj daty.
          </p>
          <ul className="space-y-1.5">
            {checkpointTemplateQ.data!.map((c, i) => {
              const st = checkpoints[c.code] ?? { enabled: true, due: isoInDays(c.offset_days) };
              return (
                <li
                  key={c.code}
                  className={`rounded-lg border px-2.5 py-2 transition-colors ${
                    st.enabled
                      ? "border-[var(--color-primary)]/30 bg-[var(--color-bg-elevated)]"
                      : "border-[var(--color-border-muted)] opacity-55"
                  }`}
                >
                  {/* Opis checkpointu w pełnej szerokości (bez ucinania w wąskim panelu). */}
                  <label className="flex cursor-pointer items-start gap-2.5">
                    <span
                      className={`mt-px grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold ${
                        st.enabled
                          ? "bg-[var(--color-primary)]/15 text-[var(--color-primary)]"
                          : "bg-[var(--color-bg-muted)] text-[var(--color-text-muted)]"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <input
                      type="checkbox"
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[var(--color-primary)]"
                      checked={st.enabled}
                      onChange={(e) =>
                        setCheckpoints((p) => ({ ...p, [c.code]: { ...st, enabled: e.target.checked } }))
                      }
                    />
                    <span className="min-w-0 flex-1 text-xs font-medium leading-snug text-[var(--color-text)]">
                      {c.label}
                    </span>
                  </label>
                  <div className="mt-1.5 flex items-center justify-between gap-2 pl-[30px]">
                    <span className="shrink-0 rounded-full bg-[var(--color-bg-muted)] px-1.5 py-px text-[10px] font-medium text-[var(--color-text-muted)]">
                      +{c.offset_days} dni
                    </span>
                    {/* Fixed-width field keeps the clear (×) next to the date instead of
                        stranding it across a full-width gap in this narrow panel. */}
                    <DatePicker
                      className="w-40 py-1 text-xs"
                      value={st.due}
                      disabled={!st.enabled}
                      onChange={(v) =>
                        setCheckpoints((p) => ({ ...p, [c.code]: { ...st, due: v } }))
                      }
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {(templateResources || ((showTenderDocs || showRouteDxf) && !winner)) && (
        <div className="space-y-2 rounded border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] p-2">
          {templateResources && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] mb-1">
                Szablony PZT/PAB/PT (repozytorium)
              </div>
              <SearchableSelect
                options={[{ value: "", label: "— bez szablonu —" }, ...templateResources]}
                value={templateResource}
                placeholder="— wybierz szablon z repozytorium —"
                onChange={(e) => setTemplateResource(e.target.value)}
              />
              <p className="mt-1 text-[11px] leading-snug text-[var(--color-text-muted)]">
                Referencja (nie kopia) do elementu repozytorium — podepnie się do zlecenia PT.
                Na liście widoczne są wyłącznie pliki z folderu repozytorium mającego „wzor” w
                nazwie lub opisie (np. „szablony_dokumentacja wzory”).
              </p>
            </div>
          )}
          {showTenderDocs && !winner && (
            <label className="flex items-center gap-1.5 text-xs text-[var(--color-text)]">
              <input
                type="checkbox"
                checked={attachTenderDocs}
                onChange={(e) => setAttachTenderDocs(e.target.checked)}
              />
              Dołącz dokumenty przetargowe do zlecenia
            </label>
          )}
          {showRouteDxf && !winner && (
            <label className="flex items-start gap-1.5 text-xs text-[var(--color-text)]">
              <input
                type="checkbox"
                className="mt-0.5 shrink-0"
                checked={attachRouteDxf}
                onChange={(e) => setAttachRouteDxf(e.target.checked)}
              />
              <span>
                Dołącz aktualną trasę (DXF) z bieżącego wariantu
                <span className="block text-[11px] leading-snug text-[var(--color-text-muted)]">
                  Najnowsza wersja trasy obowiązującego wariantu (uzgodniona koncepcja). Pomijane, gdy brak pliku DXF.
                </span>
              </span>
            </label>
          )}
        </div>
      )}

      {showProjectFileShare && connectionId && (
        <ProjectFileShareFields connectionId={connectionId} value={share} onChange={setShare} />
      )}

      {kind === "correction" && (
        <label className="flex items-center gap-1.5 text-xs text-[var(--color-text)]">
          <input type="checkbox" checked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} />
          Płatne
        </label>
      )}
      <div className="space-y-1.5">
        <Button className="w-full" disabled={!canSubmit} loading={pending} onClick={submit}>
          <PackagePlus size={13} />{" "}
          {winner
            ? "Zleć zwycięzcy (zatwierdź)"
            : kind === "correction"
              ? "Utwórz zamówienie korekcyjne"
              : "Zleć podwykonawcy (zatwierdź)"}
        </Button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="block w-full text-center text-xs text-[var(--color-text-muted)] hover:underline"
          >
            Anuluj
          </button>
        )}
      </div>
      <p className="text-[11px] leading-snug text-[var(--color-text-muted)]">
        Zapotrzebowanie zostanie od razu zatwierdzone do zamówienia i podepnie się do tego etapu.
        Jeśli zatwierdzenie się nie powiedzie (np. brak ważnej polisy OC podwykonawcy), nic nie zostanie utworzone.
      </p>
    </div>
  );
}

// Podgląd powiązanych zamówień etapu (główne + korekcyjne) z linkiem do zamówienia.
export function OrderLinksPreview({ links }: { links: DesignOrderLink[] }) {
  const role = useAuthStore((s) => s.user?.role);
  // Contractors can only reach their orders via the /contractor route (RoleGuard
  // blocks the internal /orders/:id); GK users keep the internal route.
  const orderBase = role === "contractor" ? "/contractor/orders" : "/orders";
  if (links.length === 0) return null;
  return (
    <ul className="space-y-1">
      {links.map((l) => (
        <li key={l.id} className="flex items-center gap-2 text-xs flex-wrap">
          <span
            className={`px-1.5 py-px rounded text-[10px] font-semibold uppercase ${
              l.kind === "correction"
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                : "bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
            }`}
          >
            {l.kind === "correction" ? "Korekcyjne" : "Główne"}
          </span>
          {l.order ? (
            <Link to={`${orderBase}/${l.order.id}`} className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline">
              {l.order.order_number ?? "zamówienie"} <ExternalLink size={11} />
            </Link>
          ) : (
            <span className="text-[var(--color-text-muted)]">zamówienie</span>
          )}
          {l.order && <span className="text-[var(--color-text-muted)]">· {l.order.status_display}</span>}
          {l.order?.contractor_name && <span className="text-[var(--color-text-muted)]">· {l.order.contractor_name}</span>}
          {!l.is_paid && <span className="text-green-600">· bezpłatne</span>}
        </li>
      ))}
    </ul>
  );
}
